import React, {useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { ReactComponent as DroupDown } from '../../assets/svg/drop_down.svg';
import { ReactComponent as DroupUp } from '../../assets/svg/drop_up.svg';
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { ReactComponent as Delete } from '../../assets/svg/delete_icon.svg';
import { ReactComponent as Menu } from '../../assets/svg/menu.svg';
import { ReactComponent as Edit } from '../../assets/svg/edit.svg'; 
import { Button } from "../Shared/Buttons/Button";
import { useMediaQuery } from 'react-responsive';
import { TextInput } from "../../components/Shared/Inputs/TextInput";
import { RadioButton } from "../Shared/Buttons/RadioButton";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { getSampleQuestions } from "../../services/VacancyService";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import useErrorPage from "../../hooks/useErrorPage";
import { useAlert } from "../../hooks/useAlert";
import { VacancyQuestionLoader } from "../Loaders/VacancyQuestionLoader";
import { CheckboxInput } from "../Shared/Inputs/CheckboxInput";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { 
    JobQuestion, 
    PassScoreValidation, 
    QuestionModelValidation, 
    SampleQuestion, 
    VacancyEditResponse, 
    VacancyQuestionModel 
} from "../../models/VacancyPostingModel";
import { SelectorModel } from "../../models/SelectorModel";
import { Selector } from "../Shared/Selector";

interface Props {  
    styles: any;
    jobId?: number;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    onLoad: () => Promise<VacancyEditResponse>;
    onSave: (model: VacancyEditResponse) => void;
}

export interface VacancyQuestionsEditHandle {
    saveTab: () => Promise<{ data: VacancyEditResponse } | null>;
}

const initialValidation: QuestionModelValidation = {
    questionType: {},
    question: {},
    answers: {}    
}

const initialScoreValidation: PassScoreValidation = {
    passScore: {}
}

export const VacancyQuestionsEdit = forwardRef<VacancyQuestionsEditHandle, Props>((props, ref) => {
    const jobId = props.jobId;
    const styles = props.styles;     
    const authContext = useContext(AuthContext);  
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();    
    const [openFrequentlyQuestions, setOpenFrequentlyQuestions] = useState<boolean>(false);
    const [showAddQuestion, setShowAddQuestion] = useState<boolean>(false);
    const [selectedQuestionType, setSelectedQuestionType] = useState<string>("");
    const [currentAnswer, setCurrentAnswer] = useState<string>("");
    const [currentAnswerCorrect, setCurrentAnswerCorrect] = useState<boolean>(false);
    const [currentAnswerScore, setCurrentAnswerScore] = useState<string>("");
    const [totalScore, setTotalScore] = useState<number>(0);
    const [questionList, setQuestionList] = useState<JobQuestion[]>([]);
    const [updatedQuestionList, setUpdatedQuestionList] = useState<JobQuestion[]>([]);
    const [sampleQuestions, setSampleQuestions] = useState<SampleQuestion[]>([]);
    const [jobQuestionTypes, setJobQuestionTypes] = useState<SelectorModel[]>([]);
    const [searchQuestions, setSearchQuestions] = useState("");
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
    const [editQuestionIndex, setEditQuestionIndex] = useState<number>(0);
    const [editEnable, setEditEnable] = useState<boolean>(false);    
    const [openSingleDeleteDialog, setOpenSingleDeleteDialog] = useState<boolean>(false);
    const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState<boolean>(false);
    const [questionValidation, setQuestionValidation] = useState<QuestionModelValidation>(initialValidation);
    const [scoreValidation, setScoreValidation] = useState<PassScoreValidation>(initialScoreValidation);
    const [loading, setLoading] = useState<boolean>(false);
    const topRef = useRef<HTMLDivElement>(null);
    const { Alert, openAlert } = useAlert();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);    
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [sampleQuestionIds, setSampleQuestionIds] = useState<number[]>([]);
    const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);
    
    const initialVacancyQuestionModel: VacancyQuestionModel = {
        jobId: props.jobId,
        passScore: 0,
        screenAutomatically: false,
        questionTypes:[]
    };

    const initialJobQuestion: JobQuestion = {
        jobId: props.jobId,
        questionType: "",
        question: "",
        questionTypeId: 0,
        jobQuestionId: 0,
        jobQuestionAnswers: [],
        answersArray: "",
        deleted: false 
    };

    const [newQuestion, setNewQuestion] = useState<JobQuestion>(initialJobQuestion);
    const [vacancyQuestionModel, setVacancyQuestionModel] = useState<VacancyQuestionModel>(initialVacancyQuestionModel);
    const passScoreComplete = Boolean(vacancyQuestionModel.passScore);

    useEffect(() => { 
        getVacancyQuestionDetails();        
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getVacancyQuestionDetails = async () => {
        const jobDetails = await props.onLoad();

        setVacancyQuestionModel({
            ...vacancyQuestionModel,
            passScore: jobDetails.passScore,
            screenAutomatically: jobDetails.screenAutomatically
        });
        setJobQuestionTypes(jobDetails.questionTypes ?? []);
        validateInput("passScore", String(jobDetails.passScore ?? 0));
        setQuestionList(jobDetails.questionList || []);
        setUpdatedQuestionList(jobDetails.updatedQuestionList ?? []);
        setDeletedQuestionIds(jobDetails.deletedQuestionIds ?? []);
        setSampleQuestionIds(jobDetails.sampleQuestionIds ?? []); 
        if(jobDetails.passScore == null || jobDetails.passScore === undefined) {
            calculatePassMark(jobDetails.questionList || []);
        }
    }

    const yesNoAnswers = [
        { id: 1, value: "Yes"},
        { id: 2, value: "No"}
    ];

    const questionTypeLabels: Record<number, string> = {
        1: "Yes/No",
        2: "Multiple Choice",
        3: "Single Choice",
        5: "Open",
        6: "Knockout"
    }

    async function getSampleQuestionList() {
        setLoading(true);
        await getSampleQuestions()
        .then((sampleQuestions: SampleQuestion[]) => {    
            setSampleQuestions(sampleQuestions); 
            setLoading(false);
        })
        .catch((error) => {
            toErrorPage(error);
        });
    }


    async function addSampleQuestionToJob(question: SampleQuestion) {    
        setSampleQuestionIds(prev => [...prev, question.sampleJobQuestionId]); 
        const tempUIQuestion = convertToJobQuestions(question, Math.floor(Math.random() * -1000), question.sampleJobQuestionId);
        const updatedList = [...questionList, tempUIQuestion];
        setQuestionList(updatedList);
        openAlert("success", "The question is added successfully");
    }

    const handleQuestionInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setNewQuestion((values: any) => ({ ...values, [name]: value }));

        if(questionValidation[name] && questionValidation[name]!.invalid) {
            validateInput(name, value);    
        }
    }

    const handlePassScoreInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;       
        setVacancyQuestionModel((values: any) => ({ ...values, [name]: value === "" ? 0 : value }));
        validateInput(name, value);
    }
    
    const handleAddButtonClick = (event: React.FormEvent<HTMLFormElement>) => { 
        event.preventDefault();       
        setShowAddQuestion(prevState => !prevState);
        setTotalScore(0);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const handleDiscardClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); 
        setShowAddQuestion(prevState => !prevState);
        setTotalScore(0);
        setSelectedQuestionType("");
        setNewQuestion(initialJobQuestion);
        setQuestionValidation(initialValidation);
    }

    const handleEditDiscardClick = (event: React.MouseEvent<SVGSVGElement>) => {
        event.preventDefault();
        setEditEnable(false);
        setShowAddQuestion(prevState => !prevState);        
        setSelectedQuestionType("");
        setNewQuestion(initialJobQuestion);        
        setQuestionValidation(initialValidation);     
    }

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.checked;
        setVacancyQuestionModel((values: any) => ({ ...values, [name]: value }));          
    }

    const handleDeleteClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenSingleDeleteDialog(true);
        setShowAddQuestion(prevState => !prevState);
    }

    const deleteSelectedQuetions = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenBulkDeleteDialog(true);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const handleFrequentlyQuestions = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); 
        if(sampleQuestions.length === 0)
            getSampleQuestionList();
        setOpenFrequentlyQuestions(!openFrequentlyQuestions);
    }
    
    const filteredSampleQuestions = sampleQuestions.filter(q =>
        q.question.toLowerCase().includes(searchQuestions.toLowerCase())
    );

    const handleSelectItems = (selector: string, selectedItem: SelectorModel) => {
        if(selector === "QuestionType") {
            setNewQuestion(prev => ({
                ...prev,
                questionTypeId: selectedItem.id,
                questionType: selectedItem.value                 
            }));
            setSelectedQuestionType(selectedItem.value);
            validateInput(selector, selectedItem.value);
        } 
        else {
            const isYes = selectedItem.value === "Yes";
            const answers = [
                { answer: "Yes", score: isYes ? 100 : 0 },
                { answer: "No", score: isYes ? 0 : 100 }
            ];
            setNewQuestion(prev => ({
                ...prev,
                jobQuestionAnswers: answers
            }));
        }        
    }

    const addAnswer = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); 
        const numericScore = Number(currentAnswerScore);
        if(selectedQuestionType === "Multiple Choice" && currentAnswer != "" && numericScore > 0) {
            if(totalScore + numericScore > 100) {
                openAlert("error", "The total score must be equal to 100.");
                return;
            }
            setNewQuestion(prevState => ({
                ...prevState,
                jobQuestionAnswers: [...prevState.jobQuestionAnswers, { answer: currentAnswer, score: numericScore }]
            }));
            setTotalScore(totalScore + numericScore);              
            setCurrentAnswerScore("");             
        }
        else if(selectedQuestionType === "Single Choice" && currentAnswer != "") {
            let numericScore = 0;
            if(currentAnswerCorrect) {
                numericScore = 100;
            }
            setNewQuestion(prevState => ({
                ...prevState,
                jobQuestionAnswers: [...prevState.jobQuestionAnswers, { answer: currentAnswer, score: numericScore }]
            }));            
            setCurrentAnswerCorrect(false);            
        }
        else if(selectedQuestionType == "Knockout" && currentAnswer != "") {
            let results = "Fail";
            if(currentAnswerCorrect) {
                results = "Pass";
            }
            setNewQuestion(prevState => ({
                ...prevState,
                jobQuestionAnswers: [...prevState.jobQuestionAnswers, { answer: currentAnswer, score: 0, result: results.toString() }],
            }));            
            setCurrentAnswerCorrect(false);            
        }  
        setCurrentAnswer("");
        setQuestionValidation((values: any) => ({...values, ["answers"]: {valid: true, invalid: false}})); 
    }

    const handleDeleteAnswer = (answerIndex: number) => {
        const updatedTotalScore = totalScore > 0 
        ? totalScore - newQuestion.jobQuestionAnswers[answerIndex].score 
        : 0;
        setNewQuestion(prev => ({
            ...prev,
            jobQuestionAnswers: prev.jobQuestionAnswers.filter((_, index) => index !== answerIndex)
        }));
        setTotalScore(updatedTotalScore);
    }

    const increasePassScoreValue = (score: number) => {
        let value = Number(score);
        const newScore = Math.min(value + 1, 100);
        setVacancyQuestionModel((values: any) => ({...values, ["passScore"]: newScore }));
    };

    const decreasePassScoreValue = (score: number = 0) => {
        let value = Number(score);
        const newScore = Math.max(value - 1, 0);
        setVacancyQuestionModel((values: any) => ({...values, ["passScore"]: newScore }));
    };

    const convertToJobQuestions = ( model: JobQuestion | SampleQuestion, questionId: number, sampleId?: number): JobQuestion => {
        const { questionTypeId, jobQuestionAnswers: answers } = model;
        
        const serialized = answers.map(ans => 
        questionTypeId === 6 
            ? `${ans.answer}:${ans.result || ''}` 
            : `${ans.answer}:${ans.score}`
        ).join('|');
        
        return {
            ...model,
            jobQuestionId: questionId,
            answersArray: serialized,
            jobQuestionAnswers: answers,
            sampleJobQuestionId: sampleId,
            deleted: false
        }
    }

    async function addQuestion(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateForm();
        if(validInputCount === 3) {
            const tempId = Math.floor(Math.random() * -1000);
            const newJobQuestion = {...convertToJobQuestions(newQuestion, tempId)};
            setUpdatedQuestionList(prev => [...prev, newJobQuestion]);

            const updatedList = [...questionList, newJobQuestion];
            setQuestionList(updatedList);
            setShowAddQuestion(false);
            setNewQuestion(initialJobQuestion);
            setSelectedQuestionType("");
            setTotalScore(0);
            calculatePassMark(updatedList);
            openAlert("success", "The question is added successfully");
        }
        else {
            if(totalScore != 0 && totalScore < 100) {
                openAlert("error", "The total score must be equal to 100.");
            }
            else {
                openAlert("error", "Required Fields are missing.");
            }   
        }
    }

    async function updateQuestion(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateForm();
        if (validInputCount === 3) {
            const updatedJobQuestion = { ...convertToJobQuestions(newQuestion, newQuestion.jobQuestionId) };
            const updatedList = [...questionList];
            updatedList[editQuestionIndex] = updatedJobQuestion;
            setQuestionList(updatedList);
            calculatePassMark(updatedList);

            const originalQuestion = questionList[editQuestionIndex];
            const isSampleQuestion = originalQuestion?.sampleJobQuestionId != null 
                && sampleQuestionIds.includes(originalQuestion.sampleJobQuestionId);

            if (isSampleQuestion) {
                setSampleQuestionIds(prev => prev.filter(id => id !== originalQuestion.sampleJobQuestionId));
            }

            const existsInUpdatedList = updatedQuestionList.some(q => q.jobQuestionId === updatedJobQuestion.jobQuestionId);
            if (existsInUpdatedList) {
                setUpdatedQuestionList(prev => prev.map(q =>
                    q.jobQuestionId === updatedJobQuestion.jobQuestionId ? updatedJobQuestion : q
                ));
            } else {
                setUpdatedQuestionList(prev => [...prev, updatedJobQuestion]);
            }

            setEditEnable(false);
            setShowAddQuestion(false);
            setNewQuestion(initialJobQuestion);
            openAlert("success", "The question is updated successfully");
        } else {
            openAlert("error", "Required Fields are Missing");
        }
    }

    async function deleteQuestion(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        const idToDelete = newQuestion.jobQuestionId;
        
        if(idToDelete > 0)
            setDeletedQuestionIds(prev => [...prev, idToDelete]);
        
        setUpdatedQuestionList(prev => prev.filter(q => q.jobQuestionId !== idToDelete));
        const updatedList = questionList.filter(q => q.jobQuestionId !== idToDelete);
        setQuestionList(updatedList);
        setEditEnable(false);
        setNewQuestion(initialJobQuestion);
        calculatePassMark(updatedList);
        openAlert("success", "The question is deleted successfully");
    }

    const singleDeleteCancel = () => {
        setOpenSingleDeleteDialog(false);    
    }

    const singleDeleteConfirm = () => {
        setOpenSingleDeleteDialog(false);
        deleteQuestion();
    }

    const bulkDeleteCancel = () => {
        setOpenBulkDeleteDialog(false);    
    }

    const bulkDeleteConfirm = () => {
        setOpenBulkDeleteDialog(false);
        deleteSelectedQuestions();
    }
        
    const handleQusetionEdit = (question: JobQuestion, index: number) => { 
        const questionType = questionTypeLabels[question.questionTypeId] || "";
        let parsedAnswers: { answer: string; score: number; result?: string }[] = [];
        if(question.questionTypeId === 6) {
            parsedAnswers = question.answersArray
            .split("|")
            .map((pair) => {
                const [answer, result] = pair.split(":");
                return {
                    answer,
                    score: 0,
                    result: result
                };
            });
        }
        else {
            parsedAnswers = question.answersArray
            .split("|")
            .map((pair) => {
                const [answer, score] = pair.split(":");
                const numericScore = Number(score);
                return {
                    answer,
                    score: isNaN(numericScore) ? 0 : numericScore
                };
            });
        }
        if(question.questionTypeId === 2) {
            setTotalScore(100);
        }
        else {
            setTotalScore(0);
        }
        setNewQuestion({
            jobQuestionId: question.jobQuestionId,
            jobId: jobId,
            questionType,
            question: question.question,
            questionTypeId: question.questionTypeId,            
            jobQuestionAnswers: parsedAnswers,
            answersArray: "",
            deleted: false
        });
        setEditQuestionIndex(index);
        setEditEnable(true);
        setSelectedQuestionType(questionType);
        setShowAddQuestion(true);    
        topRef.current?.scrollIntoView({ behavior: 'smooth' });    
    }

    const validateInput = (name: string, value: string) => { 
        if(name === "questionType") {  
            if(value === "") {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Question Type is required"}}));
            } 
            else {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "question") {  
            if(value === "") {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Question is required"}}));
            } 
            else {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "answers") {            
            if(value === "") {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: false, invalid: true}}));
            } 
            else {
                setQuestionValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "passScore") { 
            const score = Number(value);
            if(isNaN(score)) {
                setScoreValidation(values => ({...values, [name]: { valid: false, invalid: true, invalidMessage: "Enter numbers only" }}));
            } 
            else if(!Number.isInteger(score)) {
                setScoreValidation(values => ({...values, [name]: { valid: false, invalid: true, invalidMessage: "Decimal values are not allowed" }}));
            } 
            else if(score < 0 || score > 100) {
                setScoreValidation(values => ({...values, [name]: { valid: false, invalid: true, invalidMessage: "Enter a valid pass score (0-100)" }}));
            } 
            else {
                setScoreValidation(values => ({...values, [name]: { valid: true, invalid: false, invalidMessage: "" }}));
            }
        }
    }

    const validateForm = (): number => {
        let formValidation: QuestionModelValidation = {
            questionType: {valid:true},
            question: {valid: true},
            answers: {valid: true}            
        }

        if(newQuestion.questionType === "" || newQuestion.questionType === undefined) {
            formValidation.questionType = {valid: false, invalid: true, invalidMessage: "Question Type is required"};
        }
        if(newQuestion.questionType == "Multiple Choice" && totalScore != 100) {
            formValidation.questionType = {valid: false, invalid: true, invalidMessage: "Total Score should be equal 100"};
        }
        if(newQuestion.question === "" || newQuestion.question === undefined) { 
            formValidation.question = {valid: false, invalid: true, invalidMessage: "Question is required"};
        } 
        if(newQuestion.jobQuestionAnswers.length === 0 && newQuestion.questionType != "Open") {
            formValidation.answers = {valid: false, invalid: true};
        }        
        setQuestionValidation(formValidation);       
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount; 
    }

    const validatePassScore = (): number => {
        let scoreValidation: PassScoreValidation = {
            passScore: {valid: true}
        }
        const score = Number(vacancyQuestionModel.passScore);
        if(score === 0 || vacancyQuestionModel.passScore === undefined || vacancyQuestionModel.passScore === null) { 
            scoreValidation.passScore = {valid: true, invalid: false};
        } 
        else if(isNaN(score)) { 
            scoreValidation.passScore = {valid: false, invalid: true, invalidMessage: "Enter numbers only"};
        }
        else if(!Number.isInteger(score)) { 
            scoreValidation.passScore = {valid: false, invalid: true, invalidMessage: "Decimal values are not allowed"};
        }
        else if(score < 0 || score > 100) { 
            scoreValidation.passScore = {valid: false, invalid: true, invalidMessage: "Enter a valid pass score (0-100)"};
        }
        else {
            scoreValidation.passScore = {valid: true, invalid: false};
        }
        setScoreValidation(scoreValidation);       
        const validInputCount = Object.keys(scoreValidation).filter(key => scoreValidation[key].valid).length;
        return validInputCount; 
    }

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.dataTransfer.setData("text/plain", index.toString());
        setDragIndex(index);
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    }

    const handleDragLeave = () => {
        setDragOverIndex(null);
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
        
        if(fromIndex !== index) {
            const newList = [...questionList];
            const [movedItem] = newList.splice(fromIndex, 1);
            newList.splice(index, 0, movedItem);
            setQuestionList(newList);
        }
        
        setDragIndex(null);
        setDragOverIndex(null);
    }

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    }

    const handleSelectAll = () => {
        if(selectedQuestionIds.length === questionList.length) {
            setSelectedQuestionIds([]);
        } 
        else {
            setSelectedQuestionIds(questionList.map(q => q.jobQuestionId));
        }
    }

    const handleToggleSelect = (questionId: number) => {
        setSelectedQuestionIds(prev =>
            prev.includes(questionId)
            ? prev.filter(id => id !== questionId)
            : [...prev, questionId]
        );
    }

    async function deleteSelectedQuestions() {
        
        if (selectedQuestionIds.length === 0) return;
        const updatedList = questionList.filter(question => !selectedQuestionIds.includes(question.jobQuestionId));
        
        setUpdatedQuestionList(updatedList);
        setDeletedQuestionIds(prev => [...prev, ...selectedQuestionIds]);

        setQuestionList(updatedList);
        calculatePassMark(updatedList);
        setSelectedQuestionIds([]);
        openAlert("success", "The questions are deleted successfully.");
    }

    const completeAddingSampleQuestion = () => {
        setOpenFrequentlyQuestions(!openFrequentlyQuestions);
        calculatePassMark(questionList);
    }

    const calculatePassMark = (questionList :JobQuestion[]) => {
        var consideredQuestionTypeIds = new Set([1,2,3]);
        const validQuestionCount = questionList.filter(
            (question) => question.questionTypeId && consideredQuestionTypeIds.has(question.questionTypeId)
        ).length;
        if(validQuestionCount > 0) {
            const passMark = Math.round(Math.min((100 / validQuestionCount) * (validQuestionCount - 1), 80));
            setVacancyQuestionModel((values: any) => ({ ...values, "passScore": passMark }));
        }
        else {
            setVacancyQuestionModel((values: any) => ({ ...values, "passScore": 0 }));
        }
    }

    const saveTab = async (): Promise<{ data: VacancyEditResponse } | null> => {
        const validInputCount = validatePassScore();
        
        if (validInputCount !== 1) {
            openAlert("error", "Some mandatory fields are invalid.");
            return null;
        }

        const updatedModel: VacancyEditResponse = {
            jobId: jobId!,
            recruiterId: recruiter.recruiterId,
            sampleQuestionIds: sampleQuestionIds,
            deletedQuestionIds: deletedQuestionIds,
            updatedQuestionList: updatedQuestionList,
            passScore: vacancyQuestionModel.passScore,
            screenAutomatically: vacancyQuestionModel.screenAutomatically,
            questionList: questionList
        };

        props.onUpdateSummary({
            vacancyQuestions: true,
            passScore: passScoreComplete,
            replyMail: true
        });
        props.onSave(updatedModel);
        return { data: updatedModel };
    };

    useImperativeHandle(ref, () => ({ saveTab }));

    return(
        <div style={styles.mainWrapper}>
            <div style={isMobile ? undefined : styles.subTopicWrapper}>
                <div style={styles.subTopicWrapper.topic}>{"Vacancy Questions"}</div>
            </div>            
            {loading ? <VacancyQuestionLoader/> : 
            <form style={styles.form}>
                <div style={styles.passScore} ref={topRef}>
                    <div style={styles.passScoreText}>
                        <span>{"Pass Score"}</span>
                        {scoreValidation.passScore.invalid && <span style={styles.passScoreInvlidText}>{scoreValidation.passScore.invalidMessage}</span>}
                        {!isMobile && (scoreValidation.passScore.valid  
                            && (vacancyQuestionModel.passScore ?? 0) > 0  
                            && <div style={styles.notifyMailWrapper}>
                            <CheckboxInput name="screenAutomatically" value={vacancyQuestionModel.screenAutomatically} handleChange={handleCheckboxChange}>
                                <span style={styles.policyLabel}>{"Get notified only passed candidate"}</span>
                            </CheckboxInput>
                        </div>)}   
                    </div>
                    <div style={{...styles.passScoreInput, ...(scoreValidation.passScore.invalid && styles.passScoreInput.invalid)}}>
                        <input
                            type="text"
                            value={vacancyQuestionModel.passScore === 0 ? "" : vacancyQuestionModel.passScore}
                            style={{...styles.passScoreInput.input, ...(scoreValidation.passScore.invalid && styles.passScoreInput.input.invalid)}}
                            name={"passScore"}
                            placeholder={"%"}
                            onChange={handlePassScoreInputChange}   
                        />   
                        <div style={styles.groupIcon}>
                            <DroupUp style={styles.groupIcon.arrow} onClick={() => increasePassScoreValue(vacancyQuestionModel.passScore ?? 0)}/>
                            <DroupDown style={styles.groupIcon.arrow} onClick={() => decreasePassScoreValue(vacancyQuestionModel.passScore ?? 0)}/>
                        </div>
                    </div>
                </div>
                {isMobile 
                    && (scoreValidation.passScore.valid) 
                    && (vacancyQuestionModel.passScore ?? 0) > 0
                    && (<div style={styles.notifyMailWrapper}>
                    <CheckboxInput name="screenAutomatically" value={vacancyQuestionModel.screenAutomatically} handleChange={handleCheckboxChange}>
                        <span style={styles.policyLabel}>{"Get notified only for passed candidates"}</span>
                    </CheckboxInput>
                </div>)}
                <div style={styles.addNewQuestionWrapper}>
                    {!isMobile && "Questions"}
                    <Button style={styles.frequenltyQuestionButton} onClick={handleFrequentlyQuestions}>{"FREQUENTLY USED QUESTIONS"}</Button>
                    <Button style={styles.addNewQuestionButton} onClick={handleAddButtonClick}>{"ADD NEW QUESTION"}
                        <Plus style={styles.plusIcon}/>
                    </Button>
                </div>
                {showAddQuestion && <div style={styles.addNewquestion}>
                    <div style={styles.labelWithIcon}>                        
                        {editEnable ? <div style={styles.qusetionTittle}>{`Question ${editQuestionIndex} (Edit)`}</div> :
                        <div style={styles.qusetionTittle}>{"New Question"}</div>}
                        <DroupUp style={styles.groupIcon.icon} onClick={handleEditDiscardClick}/>
                    </div>
                    <div style={styles.groupWrapper}>
                        <div style={styles.rightMargin}>
                            <Selector
                                label={"Question Type"} 
                                placeHolder={"Select one"}
                                items={jobQuestionTypes}
                                name= {"QuestionType"}
                                value={newQuestion.questionType}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                        <div style={{...styles.inputWrapper, ...styles.leftMargin}}>
                            <label style={styles.label}>{"Question"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"question"}
                                value={newQuestion.question}
                                styles={{...styles.textInput}}
                                handleChange={handleQuestionInputChange}
                                validation={questionValidation.question}                    
                            /> 
                        </div>
                    </div>
                    <div style={styles.answerWrapper}>
                        {selectedQuestionType === "Yes/No" && <div style={styles.addYesNoAnswer}>
                            <Selector
                                label={"Answer"} 
                                placeHolder={"Select one"}
                                items={yesNoAnswers}
                                name= {"answers"}
                                value={newQuestion.answerlabel}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                                validation={questionValidation.answers}
                            />
                        </div>}
                        {selectedQuestionType == "Open" && <div style={styles.openQuestionText}>
                            {"Open questions will not be effected for the vacancy question score."}
                        </div>}
                        {selectedQuestionType === "Multiple Choice" && <div style={styles.addMultipleChoiceAnswer}>
                            <div>
                                <label style={styles.label}>{"Answer"}</label>
                                <div style={{...styles.multipleAnswerWrapper, ...(questionValidation.answers.invalid && styles.multipleAnswerWrapper.invalid)}}>
                                    <input 
                                        type={"text"} 
                                        placeholder={"Answer"} 
                                        value={currentAnswer} 
                                        onChange={(e) => setCurrentAnswer(e.target.value)} 
                                        style={{...styles.multipleAnswer, ...(questionValidation.answers.invalid && styles.multipleAnswer.invalid)}}
                                    />
                                    <input 
                                        type={"text"} 
                                        placeholder={"Score"} 
                                        value={currentAnswerScore} 
                                        onChange={(e) => setCurrentAnswerScore(e.target.value)} 
                                        style={{...styles.multipleScore, ...(questionValidation.answers.invalid && styles.multipleScore.invalid)}}
                                    />
                                </div>
                                <Button style={styles.multiplChoiceAddButton} onClick={addAnswer}>{"ADD ANSWER"}</Button>
                            </div>
                            <div style={styles.answerAndScoreMainWrapper}>
                                <div style={styles.answersLabelWrapper}>
                                    <label >{"Answers"}</label>
                                    <label >{"Score"}</label>
                                </div>
                                {newQuestion.jobQuestionAnswers.map((answer, index) =>
                                    <div style={styles.answerAndScoreWrapper} key={index}>
                                        <label>{answer.answer}</label>
                                        <div style={styles.textWithIcon}>
                                            {answer.score} 
                                            <Delete style={styles.deleteIcon} onClick={() => handleDeleteAnswer(index)} />                                                 
                                        </div>
                                    </div>
                                )} 
                                <hr/>
                                <div style={styles.totalScoreWrapper}>
                                    <label  >{"Total"}</label>
                                    <label >{`(${100 - totalScore} Remaining) ${totalScore}`}</label>
                                </div>
                            </div>
                        </div>}
                        {selectedQuestionType === "Knockout" && <div style={styles.addKnoctoutAnswer}>
                            <div style={styles.answerAndScoreMainWrapper}>
                                <label style={styles.label}>{"Answer"}</label>
                                <div style={{...styles.knockoutAnswerWrapper, ...(questionValidation.answers.invalid && styles.knockoutAnswerWrapper.invalid)}}>
                                    <input 
                                        type={"text"} 
                                        placeholder={"Answer"} 
                                        value={currentAnswer} 
                                        onChange={(e) => setCurrentAnswer(e.target.value)} 
                                        style={{...styles.knoctoutAnswer, ...(questionValidation.answers.invalid && styles.knoctoutAnswer.invalid)}}
                                    />
                                    <div style={styles.radioButtonsGrop}>
                                        <div style={styles.radioButtonGrop}>
                                            <RadioButton styles={styles.radioButton} checked={currentAnswerCorrect} handleCheck={() => setCurrentAnswerCorrect(true)}/>
                                            <label style={styles.radioButtonText}>{"Pass"}</label>
                                        </div>
                                        <div style={styles.radioButtonGrop}>
                                            <RadioButton styles={styles.radioButton} checked={!currentAnswerCorrect} handleCheck={() => setCurrentAnswerCorrect(false)}/>
                                            <label style={styles.radioButtonText}>{"Fail"}</label>
                                        </div>
                                   </div>
                                </div>
                                <Button style={styles.KnockoutAddButton} onClick={addAnswer}>{"ADD ANSWER"}</Button>
                            </div>
                            <div style={styles.knockoutAnswersWrapper}>
                                <div style={styles.knockoutAnswerLabelWrapper}>
                                    <label >{"Answers"}</label>
                                    <label >{"Result"}</label>
                                </div>
                                {newQuestion.jobQuestionAnswers.map((answer, index) => 
                                    <div key={index} style={styles.answerAndScoreWrapper}>
                                        <label>{answer.answer}</label>
                                        <div style={styles.textWithIcon}>
                                            {answer.result}
                                            <Delete style={styles.deleteIcon} onClick={() => handleDeleteAnswer(index)}/>                                                
                                        </div>
                                    </div>
                                )} 
                            </div>
                        </div>}
                        {selectedQuestionType === "Single Choice" && <div style={styles.addsingleChoiceAnswer}>
                            <div>
                                <label style={styles.label}>{"Answer"}</label>
                                <div style={{...styles.singleAnswerWrapper, ...(questionValidation.answers.invalid && styles.singleAnswerWrapper.invalid)}}>
                                    <input 
                                        type={"text"} 
                                        placeholder={"Answer"} 
                                        value={currentAnswer} 
                                        onChange={(e) => setCurrentAnswer(e.target.value)} 
                                        style={{...styles.singleAnswer, ...(questionValidation.answers.invalid && styles.singleAnswer.invalid)}}
                                    />
                                    <div style={styles.radioButtonsGrop}>
                                        <div style={styles.radioButtonGrop}>
                                            <RadioButton styles={styles.radioButton} checked={currentAnswerCorrect} handleCheck={() => setCurrentAnswerCorrect(!currentAnswerCorrect)}/>
                                            <label style={styles.radioButtonText}>{"Right"}</label>
                                        </div>
                                   </div>
                                </div>
                                <Button style={styles.KnockoutAddButton} onClick={addAnswer}>{"ADD ANSWER"}</Button>
                            </div>
                            <div style={styles.knockoutAnswersWrapper}>
                                <div style={styles.answersLabelWrapper}>
                                    <label >{"Answers"}</label>
                                    <label >{"Result"}</label>
                                </div>                                
                                {newQuestion.jobQuestionAnswers.map((answer, index) => 
                                    <div style={styles.answerAndScoreWrapper}>
                                        <label >{answer.answer}</label>
                                        <div style={styles.textWithIcon}>
                                            <RadioButton styles={styles.radioButton} checked={answer.score === 100}/>
                                            <Delete style={styles.deleteIcon} onClick={() => handleDeleteAnswer(index)}/> 
                                        </div>
                                    </div> 
                                )}                                    
                                <hr/>
                                <div style={styles.singleChoiceText}>
                                    {!newQuestion.jobQuestionAnswers.some(answer => answer.score === 100) &&
                                        <label>{"No right answer added"}</label>
                                    }
                                </div>
                            </div>
                        </div>}
                    </div>                    
                    {editEnable ? <div style={styles.buttonWrapper}>
                        <div style={styles.buttonWrapper.group}>
                            <div style={styles.buttonWrapper.topRow}>
                                <Button style={styles.buttonWrapper.update} onClick={updateQuestion}>{"UPDATE"}</Button>
                            </div>
                            <div style={styles.buttonWrapper.bottomRow}>
                                <Button style={styles.buttonWrapper.saveDraft} onClick={handleEditDiscardClick}>{"DISCARD"}</Button>
                                <Button style={styles.buttonWrapper.delete} onClick={handleDeleteClick}>{"DELETE"}</Button>
                            </div>
                        </div>                        
                    </div> :
                    <div style={styles.buttonWrapper}>
                        <Button style={styles.buttonWrapper.submit} onClick={addQuestion}>{"ADD QUESTION"}</Button>
                        <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                    </div>}
                </div>}
                <div>
                    <div style={styles.checkBoxWrapper}>
                        {questionList && questionList.length > 0 && 
                        <CheckboxInput name="selectAll" value={selectedQuestionIds.length === questionList.length} handleChange={handleSelectAll} >
                            <span  style={styles.checkBoxText}>{"Select all"}</span>
                        </CheckboxInput>}                   
                    </div>
                    {questionList.map((question, index) => {
                        const questionType = questionTypeLabels[question.questionTypeId];
                        const isChecked = selectedQuestionIds.includes(question.jobQuestionId);
                        return (
                        <div
                            key={question.jobQuestionId}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={styles.questions}
                        >
                            <div style={styles.labelWithIcon}>
                                <div style={styles.checkBoxGroup}>
                                    <CheckboxInput name={`question-${question.jobQuestionId}`} value={isChecked} handleChange={() => handleToggleSelect(question.jobQuestionId)} >
                                        <div style={styles.qusetionTittle}>{"Question "}{String(index + 1).padStart(2, "0")}</div>
                                    </CheckboxInput>
                                </div>
                                <div style={styles.iconGroup}>
                                    <Edit style={styles.groupIcon.icon} onClick={() => handleQusetionEdit(question, index)}/>
                                    <Menu style={styles.menuIcon} />
                                </div>
                            </div>
                            <div style={styles.insideQuestion}>
                                <div style={styles.questionType}>{questionType}</div>
                                <div style={styles.questionTextWrapper}>
                                    {question.question}
                                    {question.questionTypeId !== 5 && (
                                    <div style={styles.questionAnswerWrapper}>
                                        {question.answersArray?.split("|").map((answer, i) => {
                                            const [name, score] = answer.split(":");
                                            return (
                                                <div key={i} style={styles.answers}>
                                                    <span>{name}</span>
                                                    <span style={styles.answers.score}>{score}</span>
                                                </div>
                                            )
                                        })}
                                    </div>)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
                {questionList.length > 0 && <div style={styles.singleButton}>
                    {selectedQuestionIds.length === 0 && 
                    <Button style={styles.singleAddNewQuestionButton} onClick={handleAddButtonClick}>
                        {"ADD NEW QUESTION"}
                        <Plus style={styles.plusIcon}/>
                    </Button>}
                    {selectedQuestionIds.length > 0 && 
                    <Button style={styles.selectedQuetionsDeleteButton} onClick={deleteSelectedQuetions}>
                        {"DELETE SELECTED QUETIONS"}
                    </Button>}
                </div>}
            </form>}
            <PopUpWindow open={(openFrequentlyQuestions && !loading)} styles={styles.questionPopup} toggle={completeAddingSampleQuestion} closeCircle={true}>
                <div style={styles.popUpForm}>
                    <input type={"text"} placeholder={"Search for Sample Questions.."} style={styles.searchBar} onChange={(e) => setSearchQuestions(e.target.value)}/>
                    <div style={styles.questionListWrapper}>
                        {filteredSampleQuestions.map((question, index) => { 
                        const qusetionType = questionTypeLabels[question.questionTypeId];
                        return(
                            <div key={index} style={styles.popUpQuestionWrapper}>
                                <div style={styles.questionAndAnswer}>
                                    <div>{question.question}</div>
                                    {question.jobQuestionAnswers.map((answer, index) => (
                                        <div key={index} style={styles.sampleAnswers}>
                                            <span>{answer.answer}</span>{" - "}<span>{answer.score}</span>
                                        </div>
                                    ))}
                                </div>
                                <div style={styles.questionTypeAndButton}>
                                    <div>{qusetionType}</div>
                                    <Button style={styles.buttonsWrapper.add} onClick={() => addSampleQuestionToJob(question)}>{"Add"}</Button>
                                </div>
                            </div> 
                        )})}                   
                    </div> 
                    <Button style={styles.buttonsWrapper.ok} onClick={completeAddingSampleQuestion}>{"OK"}</Button>            
                </div>
            </PopUpWindow>
            <PopUpWindow styles={styles.deleteWarningDialog} open={openSingleDeleteDialog} toggle={() => setOpenSingleDeleteDialog(!openSingleDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"This question will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={singleDeleteConfirm}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={singleDeleteCancel}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <PopUpWindow styles={styles.deleteWarningDialog} open={openBulkDeleteDialog} toggle={() => setOpenBulkDeleteDialog(!openBulkDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"This questions will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>             
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={bulkDeleteConfirm}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={bulkDeleteCancel}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <Alert/>
        </div>
    )
})